import { useCallback } from 'react';
import {
  MyPermissions,
  useMyPermissions
} from '@clean-code/api/hooks/useMyPermissions';
import { useUserProfile } from '@clean-code/api/hooks/useUserProfile';
import {
  PermissionKey,
  PermissionScreen,
  SCREEN_PERMISSIONS
} from '@clean-code/types/permissions';
import {
  canOpenByLoginRole,
  isGrantedByLoginRole
} from '@clean-code/utils/loginRoleAccess';
import { useFeatureFlags, useFeatureFlagsLoaded } from '@hooks/useFeatureFlags';

const NO_PERMISSIONS: MyPermissions = {};

export const useHasPermission = () => {
  const { data = NO_PERMISSIONS } = useMyPermissions();
  return useCallback(
    (category: string, permission: string) =>
      data[`${category}.${permission}`] ?? false,
    [data]
  );
};

/**
 * Checks catalog keys. Only a company with the `rolePermissions` flag uses permissions and custom
 * roles: there `my-permissions` returns every catalog key for the caller's role and decides alone.
 * Without the flag, or against a backend without phase 5 (its `my-permissions` has no
 * `role.read`), the login role decides exactly as it did before permissions (`loginRoleAccess`).
 * `usesRolePermissions` tells the few screens whose login-role behaviour was not a per-key check.
 */
export const usePermissions = () => {
  const { userData, isLoading: profileLoading } = useUserProfile();
  const { rolePermissions } = useFeatureFlags();
  const flagsLoaded = useFeatureFlagsLoaded();
  const { data = NO_PERMISSIONS, isPending } = useMyPermissions();
  const role = userData?.role;
  const usesRolePermissions = rolePermissions && 'role.read' in data;

  const can = useCallback(
    (key: PermissionKey) =>
      usesRolePermissions
        ? data[key] === true
        : isGrantedByLoginRole(key, role),
    [data, usesRolePermissions, role]
  );
  const canAny = useCallback(
    (keys: readonly PermissionKey[]) => keys.some(can),
    [can]
  );
  const canOpen = useCallback(
    (screen: PermissionScreen) =>
      usesRolePermissions
        ? canAny(SCREEN_PERMISSIONS[screen])
        : canOpenByLoginRole(screen, role),
    [canAny, usesRolePermissions, role]
  );

  return {
    can,
    canAny,
    canOpen,
    usesRolePermissions,
    isLoading:
      profileLoading ||
      !flagsLoaded ||
      (rolePermissions && !!userData && isPending)
  };
};
